import { SpinnerPage } from "components/SpinnerPage/SpinnerPage"
import {
  AUTH_PREFIX_PATH,
  CARE_GROUP_PREFIX_PATH,
  PORTAL_PREFIX_PATH,
  PUBLIC_PREFIX_PATH,
  USES_SUBDOMAIN,
} from "configs/AppConfig"
import { useIsAuthenticated } from "hooks/auth"
import { packReturnURL } from "hooks/returnUrl"
import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from "react-router"
import { trackPageView } from "tracking/pageViews"
import { lazily } from "utils"

import { NATIONAL_SLUG } from "@shared/constants/names"

import { RootLayout } from "./layout"

const { AuthRouter } = lazily(() => import("./auth/router"))
const { default: PortalLayout } = lazily(() => import("./portal/layout"))
const { PublicCareGroupRouter } = lazily(() => import("./public/care-group-router"))
const { PracticeSearchRouter } = lazily(() => import("./public/practice-search-router"))
const { PublicRouter } = lazily(() => import("./public/router"))

// Redirect / to /PORTAL_PREFIX_PATH when not using subdomains (localhost) or for non-practice subdomain (e.g. www.mijnpraktijk.nl)
const MAIN_SUBDOMAINS = ["www", "portal", "dev", "staging", "mijnpraktijk"]
const REDIRECT_ROOT_TO_PORTAL = !USES_SUBDOMAIN || MAIN_SUBDOMAINS.includes(window.location.host.split(".")[0])

const PUBLIC_PATH = `${PUBLIC_PREFIX_PATH}${USES_SUBDOMAIN ? "" : "/:practiceSlug"}`

const Portal = () => {
  const isAuthenticated = useIsAuthenticated()

  if (!isAuthenticated) {
    // If not authenticated, go to the login page with possible redirect back
    return (
      <Navigate
        to={{
          // navigate to the login page
          pathname: `${AUTH_PREFIX_PATH}/login`,

          // In case when we aren't already in the "/auth" path stack, we want
          // to return to the previous page after login. We can do this by
          // packing the current location into a query string which is handled
          // by <Login> component.
          search: !location.pathname.startsWith("/auth") ? packReturnURL(location).toString() : "",
        }}
        replace
      />
    )
  }

  // Otherwise render the authenticated Portal
  return <PortalLayout />
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {REDIRECT_ROOT_TO_PORTAL && (
        <Route
          path="/"
          element={<Navigate to={{ pathname: PORTAL_PREFIX_PATH, hash: location.hash }} replace />}
          HydrateFallback={SpinnerPage}
        />
      )}
      <Route path={`${AUTH_PREFIX_PATH}/*`} element={<AuthRouter />} HydrateFallback={SpinnerPage} />
      <Route path={`${PORTAL_PREFIX_PATH}/*`} element={<Portal />} HydrateFallback={SpinnerPage} />
      <Route path={`${CARE_GROUP_PREFIX_PATH}/*`} element={<PublicCareGroupRouter />} HydrateFallback={SpinnerPage} />
      <Route path={`${NATIONAL_SLUG}/*`} element={<PracticeSearchRouter />} HydrateFallback={SpinnerPage} />
      <Route path={`${PUBLIC_PATH}/*`} element={<PublicRouter />} HydrateFallback={SpinnerPage} />
    </Route>
  )
)

// Subscribe to the router to track the page views across the whole application.
router.subscribe((state) => {
  // Track the page view. This will send the information with the user data filled in.
  // It's not an ideal dataset, but the nature of react-router makes it very difficult
  // to get better information. What we would like to get get are paths likes "/user/:id"
  // instead, the router, only informs us about "/user/123".
  trackPageView(state.location.pathname)
})
